import type { StaffLeadClub } from '../../types/leadSplit.js';

// THE STAFF CLUB SCOPE (KyckConnect all-clients Leads page): which clubs a Brand and/or Group choice leaves. Pure, used by the
// SERVER to decide the query scope and by the page to narrow its Club list, so the two cannot disagree. ⚠️ STAFF ONLY: the portal
// schema has no brandId / groupId, so a club's viewer can never reach this.

export interface ClubAccountLike {
  id: string;
  name: string;
  accountType: string;
  demoAccount?: boolean;
}

export const LEAD_CLUB_TYPES = ['location', 'dealership'] as const;

/** Operational accounts with their brand and group, from the canonical maps (computeBrandAncestorMap / computeGroupAncestorMap). */
export function buildStaffClubs(
  accounts: readonly ClubAccountLike[],
  brandOf: ReadonlyMap<string, string | null>,
  groupOf: ReadonlyMap<string, string | null>,
): StaffLeadClub[] {
  const nameOf = new Map(accounts.map((a) => [a.id, a.name]));
  return accounts
    .filter((a) => (LEAD_CLUB_TYPES as readonly string[]).includes(a.accountType))
    .map((a) => {
      const brandId = brandOf.get(a.id) ?? null;
      const groupId = groupOf.get(a.id) ?? null;
      return { id: a.id, name: a.name, brandId, brandName: brandId ? (nameOf.get(brandId) ?? null) : null, groupId, groupName: groupId ? (nameOf.get(groupId) ?? null) : null, demo: a.demoAccount === true };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The clubs left by a Brand and/or Group choice (both = both must hold). No choice = every club. */
export function clubsMatching(clubs: readonly StaffLeadClub[], f: { brandId?: string; groupId?: string }): StaffLeadClub[] {
  return clubs.filter((c) => (!f.brandId || c.brandId === f.brandId) && (!f.groupId || c.groupId === f.groupId));
}

export interface ScopeOption {
  id: string;
  name: string;
}

/** The distinct brands (or groups) among the given clubs, by name. */
export function scopeOptions(clubs: readonly StaffLeadClub[], kind: 'brand' | 'group'): ScopeOption[] {
  const seen = new Map<string, string>();
  for (const c of clubs) {
    const id = kind === 'brand' ? c.brandId : c.groupId;
    const name = kind === 'brand' ? c.brandName : c.groupName;
    if (id && name) seen.set(id, name);
  }
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The account ids a staff query is scoped to. Returns null when there is no Brand/Group choice (the caller keeps its own scope).
 * An explicit club must itself be inside the brand/group, else nothing is read.
 */
export function staffScopeIds(clubs: readonly StaffLeadClub[], f: { brandId?: string; groupId?: string; accountId?: string }): string[] | null {
  if (!f.brandId && !f.groupId) return null;
  // ⚠️ A Brand / Group rollup leaves the DEMO club out; asking for that club by name still reads it.
  const ids = clubsMatching(clubs, f).filter((c) => !c.demo || c.id === f.accountId).map((c) => c.id);
  return f.accountId ? ids.filter((id) => id === f.accountId) : ids;
}
